'use client';
import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useState, forwardRef } from 'react';
import { merge } from './merge.js';
import { omit } from './omit.js';
import { useIsomorphicLayoutEffect } from '../../../hooks/use-isomorphic-layout-effect.js';
import { isNotProduction } from '../../../../core/utilities/env.utils.js';

function _define_property(e, r, t) {
    return r in e ? Object.defineProperty(e, r, {
        value: t,
        enumerable: true,
        configurable: true,
        writable: true
    }) : e[r] = t, e;
}
function _object_spread(e) {
    for(var r = 1; r < arguments.length; r++){
        var t = null != arguments[r] ? arguments[r] : {}, s = Object.keys(t);
        "function" == typeof Object.getOwnPropertySymbols && (s = s.concat(Object.getOwnPropertySymbols(t).filter(function(e) {
            return Object.getOwnPropertyDescriptor(t, e).enumerable;
        }))), s.forEach(function(r) {
            _define_property(e, r, t[r]);
        });
    }
    return e;
}
let u = isNotProduction(), c = 'object' == typeof window;
function createResponsiveSystem(o) {
    let { defaultScreenClass: p, breakpoints: f, cascadeMode: d = 'no-cascade' } = o;
    if (u) {
        let e = Object.values(f);
        if (e.length < 2) throw Error('ScreenClassConfigurationError - `breakpoints` must have at least 2 keys e.g. `{ mobile: 320, desktop: Infinity }`');
        if (1 !== e.filter((e)=>e === 1 / 0).length) throw Error('ScreenClassConfigurationError - `breakpoints` must have exactly 1 entry with a value of `Infinity` for the maximum pixel-width e.g. `{ mobile: 320, desktop: Infinity }`');
    }
    let m = Object.entries(f).sort(([, e], [, r])=>e > r ? 1 : 0), v = m.map(([e])=>e);
    function getMobileFirstScreenClasses(e) {
        return v.slice(0, v.indexOf(e) + 1);
    }
    function getDesktopFirstScreenClasses(e) {
        return v.slice(v.indexOf(e)).reverse();
    }
    let h = createContext(void 0), y = createContext(false), { Provider: b } = h;
    function useScreenClass() {
        let e = useContext(h);
        if (void 0 === e) {
            if (u) throw Error("`useScreenClass` may only be used inside of a ScreenClassProvider. Make sure that you've rendered a ScreenClassProvider above this component your tree (usually folks render ScreenClassProvider near the root of their app). Returning the default screen class.");
            return p;
        }
        return e;
    }
    function useResponsiveProps(e) {
        let r = useContext(h);
        if (void 0 === r) {
            if (u) throw Error("`useResponsiveProps` may only be used inside of a ScreenClassProvider. Make sure that you've rendered a ScreenClassProvider above this component your tree (usually folks render ScreenClassProvider near the root of their app). Returning the default props with no overrides.");
            return omit(e, v);
        }
        let t = omit(e, v), n = [];
        switch(d){
            case 'mobile-first':
                n = getMobileFirstScreenClasses(r);
                break;
            case 'desktop-first':
                n = getDesktopFirstScreenClasses(r);
                break;
            default:
                n = [
                    r
                ];
        }
        return merge([
            t,
            ...n.map((r)=>{
                var t;
                return null !== (t = e[r]) && void 0 !== t ? t : {};
            })
        ]);
    }
    function responsive(r) {
        let s = forwardRef((t, s)=>jsx(r, _object_spread({
                ref: s
            }, useResponsiveProps(t))));
        return s.displayName = void 0 !== r.displayName ? `Responsive(${r.displayName})` : 'ResponsiveComponent', s;
    }
    return {
        ScreenClassContext: h,
        ScreenClassProvider: ({ children: r })=>{
            let [t, s] = useState(p), [o, a] = useState(false);
            return useIsomorphicLayoutEffect(()=>{
                if (!c) return;
                let e = m.map(([e, r], t)=>{
                    let n = t > 0 ? m[t - 1][1] + 1 : 0, o = [];
                    0 !== n && o.push(`(min-width: ${n}px)`), r !== 1 / 0 && o.push(`(max-width: ${r}px)`);
                    let i = o.join(' and '), l = window.matchMedia(i);
                    return l.matches && (s(e), a(true)), [
                        e,
                        l
                    ];
                }), r = [];
                return e.forEach(([e, t])=>{
                    let listener = (r)=>{
                        r.matches && (s(e), a(true));
                    };
                    t.addListener(listener), r.push([
                        t,
                        listener
                    ]);
                }), ()=>r.forEach(([e, r])=>e.removeListener(r));
            }, []), jsx(y.Provider, {
                value: o,
                children: jsx(b, {
                    value: t,
                    children: r
                })
            });
        },
        useResponsiveProps,
        useScreenClass,
        useIsScreenClassInitialized: function() {
            return useContext(y);
        },
        responsive
    };
}

export { createResponsiveSystem };
